import { useMemo } from "react";
import { FaRegEdit } from "react-icons/fa";
import {
  HiOutlineExclamationTriangle,
  HiOutlineMagnifyingGlass,
  HiOutlinePlus,
  HiOutlineTag,
  HiOutlineTrash,
} from "react-icons/hi2";
import { IClinicTagConfig } from "@/api_framework/api_modals/ClinicTagConfig";
import TableComponent, { type TableColumn } from "@/app/components/ui/Reusable/TableComponent";
import StructuredTableSkeleton from "@/app/components/ui/Skeletons/StructuredTableSkeleton";
import { useUtils } from "@/app/hooks/useUtils";
import { ConfirmModal, PersonaExpressionText, RibbonPreview, StatusBadge } from "../HomeTileConfig/components";
import { SearchInput } from "../HomeTileConfig/controls";
import {
  primaryButtonClassName,
  secondaryButtonClassName,
  STATUS_COLUMN_FILTER_OPTIONS,
  StatusFilter,
} from "../HomeTileConfig/interface";
import { fromPersonaRule } from "../HomeTileConfig/personaExpression";

const EmptyCell = () => <span className="text-xs text-slate-400">—</span>;

export const useClinicTagColumns = ({
  onEdit,
  onDelete,
  statusFilter,
  onStatusFilterChange,
}: {
  onEdit: (configId: string) => void;
  onDelete: (config: IClinicTagConfig) => void;
  statusFilter: StatusFilter;
  onStatusFilterChange: (next: StatusFilter) => void;
}): TableColumn<IClinicTagConfig>[] => {
  const { veticDateFormat } = useUtils();

  return useMemo<TableColumn<IClinicTagConfig>[]>(
    () => [
      {
        id: "persona_combination",
        header: "Persona Combination",
        minWidth: 280,
        cell: ({ row }) => {
          const combination = fromPersonaRule(row.persona_rule);
          return combination ? <PersonaExpressionText value={combination} /> : <EmptyCell />;
        },
        headerClassName: "text-slate-500",
      },
      {
        id: "chip_txt",
        header: "Tag Text",
        width: 160,
        cell: ({ row }) =>
          row.chip_txt ? <RibbonPreview text={row.chip_txt} isActive={row.status === "ACTIVE"} /> : <EmptyCell />,
        headerClassName: "text-slate-500",
      },
      {
        id: "status",
        header: "Status",
        width: 130,
        overflow: "visible",
        filter: {
          options: STATUS_COLUMN_FILTER_OPTIONS,
          value: statusFilter,
          onChange: (next) => onStatusFilterChange(next as StatusFilter),
        },
        cell: ({ row }) => <StatusBadge status={row.status} />,
        headerClassName: "text-slate-500",
      },
      {
        id: "created_at",
        header: "Created At",
        width: 170,
        cell: ({ row }) => {
          const createdAt = veticDateFormat(row.created_at);
          return createdAt ? (
            <span className="whitespace-nowrap text-xs text-slate-600">{createdAt}</span>
          ) : (
            <EmptyCell />
          );
        },
        headerClassName: "text-slate-500",
      },
      {
        id: "updated_at",
        header: "Updated At",
        width: 170,
        cell: ({ row }) => {
          const updatedAt = veticDateFormat(row.updated_at);
          return updatedAt ? (
            <span className="whitespace-nowrap text-xs text-slate-600">{updatedAt}</span>
          ) : (
            <EmptyCell />
          );
        },
        headerClassName: "text-slate-500",
      },
      {
        id: "updated_by",
        header: "Updated By",
        minWidth: 180,
        cell: ({ row }) =>
          row.updated_by ? (
            <span className="block truncate text-xs text-slate-600" title={row.updated_by}>
              {row.updated_by}
            </span>
          ) : (
            <EmptyCell />
          ),
        headerClassName: "text-slate-500",
      },
      {
        id: "action",
        header: "Action",
        align: "center",
        width: 130,
        cell: ({ row }) => (
          <div className="flex items-center justify-center gap-1">
            <button
              type="button"
              onClick={() => onEdit(row.id)}
              className="rounded-lg border border-transparent p-2 text-[#193A6A] transition-colors hover:border-slate-200 hover:bg-slate-50"
              aria-label={`Edit configuration ${row.id}`}
              title="Edit configuration"
            >
              <FaRegEdit className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => onDelete(row)}
              className="rounded-lg border border-transparent p-2 text-rose-600 transition-colors hover:border-rose-100 hover:bg-rose-50"
              aria-label={`Delete rule ${row.id}`}
              title="Delete rule"
            >
              <HiOutlineTrash className="h-4 w-4" />
            </button>
          </div>
        ),
        headerClassName: "text-slate-500 text-center",
      },
    ],
    [onEdit, onDelete, statusFilter, onStatusFilterChange, veticDateFormat],
  );
};

export const ClinicTagFilters = ({
  searchTerm,
  onSearchChange,
}: {
  searchTerm: string;
  onSearchChange: (next: string) => void;
}) => (
  <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 border-b border-slate-100 bg-slate-50/60 px-5 py-3">
    <SearchInput
      value={searchTerm}
      onChange={onSearchChange}
      placeholder="Search persona or tag"
      ariaLabel="Search clinic tag configurations"
      clearLabel="Clear configuration search"
    />
  </div>
);

export const ClinicTagTable = ({
  columns,
  configs,
  isLoading,
  error,
  hasFilters,
  clinicName,
  onRetry,
  onClearFilters,
  onAddRule,
}: {
  columns: TableColumn<IClinicTagConfig>[];
  configs: IClinicTagConfig[];
  isLoading: boolean;
  error: string | null;
  hasFilters: boolean;
  clinicName: string;
  onRetry: () => void;
  onClearFilters: () => void;
  onAddRule: () => void;
}) => {
  if (isLoading) {
    return (
      <div className="px-5 py-4">
        <StructuredTableSkeleton columns={columns.length} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center px-5 py-14 text-center">
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-amber-50 text-amber-600">
          <HiOutlineExclamationTriangle className="h-6 w-6" />
        </span>
        <h3 className="mt-3 text-sm font-semibold text-gray-900">{error}</h3>
        <button type="button" onClick={onRetry} className={`mt-4 ${secondaryButtonClassName}`}>
          Retry
        </button>
      </div>
    );
  }

  if (!configs.length) {
    return (
      <div className="flex flex-col items-center px-5 py-14 text-center">
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#EEF5FF] text-[#193A6A]">
          {hasFilters ? <HiOutlineMagnifyingGlass className="h-6 w-6" /> : <HiOutlineTag className="h-6 w-6" />}
        </span>
        <h3 className="mt-3 text-sm font-semibold text-gray-900">
          {hasFilters ? "No configuration found" : "No configuration yet"}
        </h3>
        <p className="mt-1 max-w-sm text-xs leading-5 text-gray-500">
          {hasFilters
            ? "No configuration matches the current search or filters. Try clearing them."
            : `No tag has been configured for ${clinicName} yet.`}
        </p>
        {hasFilters ? (
          <button type="button" onClick={onClearFilters} className={`mt-4 ${secondaryButtonClassName}`}>
            Clear filters
          </button>
        ) : (
          <button type="button" onClick={onAddRule} className={`mt-4 ${primaryButtonClassName}`}>
            <HiOutlinePlus className="h-4 w-4" />
            Add Rule
          </button>
        )}
      </div>
    );
  }

  return (
    <TableComponent<IClinicTagConfig>
      columns={columns}
      data={configs}
      getRowId={(row) => row.id}
      containerClassName="!mr-0 !rounded-none !border-0 bg-white"
      scrollContainerClassName="overflow-x-auto max-h-[calc(100vh-320px)]"
      minTableWidthClassName="min-w-full"
      tableClassName="min-w-full divide-y divide-slate-100"
      stickyHeader
      wrapCells
    />
  );
};

export const DeleteTagRuleModal = ({
  target,
  clinicName,
  isLoading,
  onConfirm,
  onClose,
}: {
  target: IClinicTagConfig | null;
  clinicName: string;
  isLoading: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) => (
  <ConfirmModal
    isOpen={Boolean(target)}
    title="Delete this rule?"
    description={
      <>
        {target?.persona_rule && (
          <span className="mb-1.5 block rounded-md bg-slate-50 px-2 py-1">
            <PersonaExpressionText value={fromPersonaRule(target.persona_rule)} />
          </span>
        )}
        The tag{target?.chip_txt ? ` “${target.chip_txt}”` : ""} will stop showing on {clinicName}. This cannot be
        undone.
      </>
    }
    isLoading={isLoading}
    onConfirm={onConfirm}
    onClose={onClose}
  />
);
